import { setDirectoryMembership } from "../access/patientDirectory";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { doc, getDoc, onSnapshot } from "../access/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";
const ClinicContext = createContext();
export function ClinicProvider({ children }) {
  const [clinicId, setClinicId] = useState(null),
    [clinicData, setClinicData] = useState(null),
    [currentUser, setCurrentUser] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [availableClinics, setAvailableClinics] = useState([]),
    [availableClinicIds, setAvailableClinicIds] = useState([]),
    [refresh, setRefresh] = useState(0);
  const selected = useRef(
      new URLSearchParams(window.location.search).get("consultorio"),
    ),
    sequence = useRef(0);
  const requestedClinic = useRef(selected.current);
  const refreshClinic = useCallback(() => setRefresh((value) => value + 1), []);
  useEffect(() => {
    let profileVersion = 0;
    let unsubscribeProfile = () => {},
      unsubscribeMember = () => {},
      active = true;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const revision = ++sequence.current;
      unsubscribeProfile();
      unsubscribeMember();
      setDirectoryMembership(null, null);
      setLoading(true);
      setCurrentUser(null);
      setClinicData(null);
      setClinicId(null);
      setAvailableClinics([]);
      setAvailableClinicIds([]);
      setError("");
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        await httpsCallable(functions, "ensureClinicAccount")({});
        if (!active || revision !== sequence.current) return;
        unsubscribeProfile = onSnapshot(
          doc(db, "users", user.uid),
          async (profile) => {
            const profileRevision = ++profileVersion;
            if (
              !profile.exists() ||
              !active ||
              revision !== sequence.current ||
              profileRevision !== profileVersion
            )
              return;
            const global = profile.data(),
              ids = [
                ...new Set(
                  [...(global.clinicIds || []), global.clinicId].filter(
                    Boolean,
                  ),
                ),
              ];
            try {
              const entries = await Promise.all(
                ids.map(async (id) => {
                  const member = await getDoc(
                    doc(db, "clinics", id, "users", user.uid),
                  );
                  if (!member.exists() || member.data().activo === false)
                    return null;
                  const clinic = await getDoc(doc(db, "clinics", id));
                  return clinic.exists() ? { id, ...clinic.data() } : null;
                }),
              );
              if (
                !active ||
                revision !== sequence.current ||
                profileRevision !== profileVersion
              )
                return;
              const clinics = entries.filter(Boolean),
                allowed = clinics.map((clinic) => clinic.id);
              setAvailableClinics(clinics);
              setAvailableClinicIds(allowed);
              const next = allowed.includes(requestedClinic.current)
                ? requestedClinic.current
                : allowed.includes(selected.current)
                  ? selected.current
                  : allowed.includes(global.clinicId)
                    ? global.clinicId
                    : allowed[0];
              if (
                requestedClinic.current &&
                !allowed.includes(requestedClinic.current) &&
                profile.metadata?.fromCache
              )
                return;
              unsubscribeMember();
              if (!next) {
                setClinicId(null);
                setCurrentUser(null);
                setClinicData(null);
                setLoading(false);
                return;
              }
              selected.current = next;
              if (window.location.pathname === "/") {
                const url = new URL(window.location.href);
                url.searchParams.set("consultorio", next);
                window.history.replaceState(
                  {},
                  "",
                  url.pathname + url.search + url.hash,
                );
              }
              const clinic = clinics.find((entry) => entry.id === next);
              let whatsapp = null;
              try {
                const value = await getDoc(
                  doc(db, "clinics", next, "settings", "whatsapp"),
                );
                if (value.exists()) whatsapp = value.data();
              } catch {} // Optional integration configuration has its own access policy.
              if (
                !active ||
                revision !== sequence.current ||
                profileRevision !== profileVersion
              )
                return;
              setClinicData({ ...clinic, whatsapp });
              unsubscribeMember = onSnapshot(
                doc(db, "clinics", next, "users", user.uid),
                (member) => {
                  if (
                    !active ||
                    revision !== sequence.current ||
                    selected.current !== next
                  )
                    return;
                  if (!member.exists() || member.data().activo === false) {
                    setDirectoryMembership(null, null);
                    setCurrentUser(null);
                    setClinicId(null);
                    setClinicData(null);
                    setError("Tu acceso a este consultorio está desactivado.");
                  } else {
                    setDirectoryMembership(next, member.data());
                    setCurrentUser((before) => {
                      const memberData = member.data();
                      const candidate = {
                        id: member.id,
                        uid: member.id,
                        ...memberData,
                        isOwner: [clinic.ownerId, clinic.ownerUid].includes(user.uid) || memberData.isOwner === true,
                      };
                      return JSON.stringify(before) ===
                        JSON.stringify(candidate)
                        ? before
                        : candidate;
                    });
                    if (requestedClinic.current === next)
                      requestedClinic.current = null;
                    setClinicId(next);
                  }
                  setLoading(false);
                },
                (caught) => {
                  setError(caught.message);
                  setCurrentUser(null);
                  setClinicId(null);
                  setLoading(false);
                },
              );
            } catch (caught) {
              if (
                active &&
                revision === sequence.current &&
                profileRevision === profileVersion
              ) {
                setError(caught.message);
                setLoading(false);
              }
            }
          },
          (caught) => {
            setError(caught.message);
            setLoading(false);
          },
        );
      } catch (caught) {
        if (active && revision === sequence.current) {
          setError(caught.message);
          setLoading(false);
        }
      }
    });
    return () => {
      active = false;
      ++sequence.current;
      unsubscribe();
      unsubscribeProfile();
      unsubscribeMember();
    };
  }, [refresh]);
  const switchClinic = useCallback(
    async (id) => {
      if (availableClinicIds.includes(id) && id !== selected.current) {
        requestedClinic.current = null;
        selected.current = id;
        const url = new URL(window.location.href);
        url.searchParams.set("consultorio", id);
        window.history.replaceState(
          {},
          "",
          url.pathname + url.search + url.hash,
        );
        setCurrentUser(null);
        setClinicId(null);
        setLoading(true);
        refreshClinic();
      }
    },
    [availableClinicIds, refreshClinic],
  );
  return (
    <ClinicContext.Provider
      value={{
        clinicId,
        clinicData,
        currentUser,
        loading,
        error,
        availableClinicIds,
        availableClinics,
        switchClinic,
        refreshClinic,
      }}
    >
      {children}
    </ClinicContext.Provider>
  );
}
export function useClinic() {
  return useContext(ClinicContext);
}
